import PostAddIcon from "@mui/icons-material/PostAdd";
import CropSquareIcon from "@mui/icons-material/CropSquare";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import MoveToInboxOutlinedIcon from "@mui/icons-material/MoveToInboxOutlined";
import EastIcon from "@mui/icons-material/East";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import AssignmentReturnOutlinedIcon from "@mui/icons-material/AssignmentReturnOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";

import { ENTRY_MODE } from "./workOrderHelpers";

// The tree the New Work Order FAB walks: how many assets, then which movement.
//
// BUILT FROM CONFIG, NOT HARDCODED. The third level IS the org's movementType
// rows, and the code this hands back goes straight into the form as the chosen
// movement type -- so a hardcoded list would be a second copy of a list the
// server already owns, and the two would disagree the first time an org
// retired a type or the first time someone typed INTER_MOVEMENT where the
// config says intermovement. Labels, order and codes all come from the fetched
// rows; only the icons are ours, because config has no icon column.
//
// The icon map is keyed by the SAME codes the seed writes. An unknown code
// still gets a node -- with the generic work order icon -- because a movement
// type this file has not met is a type the org configured and the user is
// entitled to raise, and silently dropping it would be a missing option with
// no error anywhere.

export const ROOT_CODE = "NEW_WO";

export const MOVEMENT_ICONS = {
    vendorIntake: <MoveToInboxOutlinedIcon />,
    forward: <EastIcon />,
    intermovement: <SyncAltIcon />,
    delivery: <LocalShippingOutlinedIcon />,
    return: <AssignmentReturnOutlinedIcon />,
};

export const movementIcon = (code) =>
    MOVEMENT_ICONS[code] || <AssignmentOutlinedIcon />;

// The SHORT labels the breadcrumb uses, where the full one is too long for a
// crumb. Only the codes that need shortening appear; everything else falls
// back to its configured label, which is what the crumb renders by default.
const MOVEMENT_SHORT_LABELS = {
    vendorIntake: "Intake",
    intermovement: "Inter",
};

// The movement level, one node per configured type.
const movementNodes = (movementTypes = []) =>
    (Array.isArray(movementTypes) ? movementTypes : [])
        .filter((type) => type?.code)
        .map((type) => ({
            code: type.code,
            label: type.label || type.code,
            shortLabel: MOVEMENT_SHORT_LABELS[type.code] || type.label || type.code,
            icon: movementIcon(type.code),
        }));

/**
 * @param {Array} movementTypes The org's movementType config rows.
 * @returns {import("../../../Components/TreeFab/treeFabTokens").TreeFabNode|null}
 *          null while the config is still loading -- see the note below.
 */
export const buildNewWorkOrderTree = (movementTypes = []) => {
    const movements = movementNodes(movementTypes);
    // NO TREE UNTIL THERE IS SOMETHING TO WALK. A FAB that opens onto two
    // options which then open onto nothing is a dead end the user reaches in
    // two clicks; not rendering it until the config lands is a control that
    // appears a moment late and always works.
    if (!movements.length) return null;

    return {
        code: ROOT_CODE,
        label: "New Work Order",
        shortLabel: "Work order",
        icon: <PostAddIcon />,
        children: [
            {
                code: ENTRY_MODE.SINGLE,
                label: "For Single Asset",
                shortLabel: "Single",
                icon: <CropSquareIcon />,
                children: movements,
            },
            {
                code: ENTRY_MODE.PASTE,
                label: "For Multiple Assets",
                shortLabel: "Multiple",
                icon: <LayersOutlinedIcon />,
                children: movements,
            },
        ],
    };
};

/**
 * A finished walk -> what the New Work Order page needs.
 *
 * The scope level's codes ARE the form's entry modes (ENTRY_MODE.SINGLE /
 * .PASTE) rather than words of this file's own, so nothing has to translate
 * between the two -- the classic shape where a value declared in one place is
 * checked against a list in another, and the two drift.
 */
export const workOrderRouteFromSelection = ({ codes = [] } = {}) => {
    const [, scope, movementTypeCode] = codes;
    return {
        entryMode: scope === ENTRY_MODE.PASTE ? ENTRY_MODE.PASTE : ENTRY_MODE.SINGLE,
        // Absent when the walk somehow ends early. The form treats an empty
        // code exactly as it treats a form nobody has touched: the dropdown
        // opens unchosen rather than on a type the user did not pick.
        movementTypeCode: movementTypeCode || "",
    };
};
